<script lang="ts" setup>
import type { TSchoolLocation } from "@/assets/js/globalTypes";
import { useSchoolStore } from "@/store/school";
import MaterialSymbolsPinDropOutlineRounded from "~icons/material-symbols/pin-drop-outline-rounded";
import SchoolLocationHeader from "@/components/school/school-setup/school-location/SchoolLocationHeader.vue";
import SchoolLocationContent from "@/components/school/school-setup/school-location/SchoolLocationContent.vue";
import guideImage from "@/assets/image/school-setup-guide-woman.png";
import { $t } from "@/i18n";
import {
	openEditSchoolLocationModal,
	openCreateSchoolLocationModal,
	openConfirmDialog,
} from "@/components/modals/modalDefinitions";
import { useToastStore } from "@/store/toast";
import api from "@/api";
import { useSessionStore } from "@/store/session";
import { useDefaultStore } from "@/store";

definePage({
	name: "school.school-setup.school-location",
});

const schoolStore = useSchoolStore();
// const locations = ref<TSchoolLocation[]>([]);
const suggestions = ref<TSchoolLocation[]>([]);
const toastStore = useToastStore();
const sessionStore = useSessionStore();
const isLoadingArchive = ref(false);
const isLoading = ref(false);
const dataTableColumns = computed(() => [
	{
		header: $t("app.school.schoolSetup.schoolLocations.campusName"),
		field: "slc_name",
	},
	{
		header: $t("app.school.schoolSetup.schoolLocations.address"),
		field: "slc_address",
	},
	{
		header: $t("app.school.schoolSetup.schoolLocations.city"),
		field: "cit_name",
	},
]);
const defaultStore = useDefaultStore();
const speechBubble = $t("app.school.schoolSetup.schoolLocations.guideText", {
	supportEmail: defaultStore.supportEmail,
});

function getSuggestions(q: string) {
	q = q.trim().toLowerCase();
	if (schoolStore.schoolData && schoolStore.schoolData.locations) {
		suggestions.value = q
			? schoolStore.schoolData.locations.filter((location) =>
					location.slc_name.toLowerCase().includes(q),
				)
			: schoolStore.schoolData.locations;
	}
}

// function handleOptionSelect(selected: TSchoolLocation) {
// 	openEditSchoolLocationModal(selected);
// }

function editSchoolLocation(item: TSchoolLocation) {
	openEditSchoolLocationModal(item);
}

// function handleSearchOnClick(q: string | TSchoolLocation) {
// 	q = typeof q === "string" ? q.trim().toLowerCase() : q.slc_name.toLowerCase();

// 	if (schoolStore.schoolData && schoolStore.schoolData.locations) {
// 		locations.value = q
// 			? schoolStore.schoolData.locations.filter((location) =>
// 					location.slc_name.toLowerCase().includes(q),
// 				)
// 			: schoolStore.schoolData.locations;
// 	}
// }

function deleteLocation(location: TSchoolLocation) {
	openConfirmDialog(
		$t("app.school.schoolSetup.schoolLocations.confirmArchivingDailog.title"),
		$t("app.school.schoolSetup.schoolLocations.confirmArchivingDailog.text", {
			item: location.slc_name,
		}),
		async () => {
			isLoadingArchive.value = true;
			try {
				const res = await api.removeSchoolLocation({
					slc_id: location.slc_id,
				});
				console.warn(res.data);
				schoolStore.schoolData = await schoolStore.fetchSchoolData(
					sessionStore.userData.scl_id,
				);
				toastStore.openToastSuccess(res.data.message);
			} catch (error) {
				console.error(error);
				toastStore.openToastError(error.response?.data?.message);
			} finally {
				isLoadingArchive.value = false;
			}
		},
	);
}

watch(
	() => schoolStore.schoolData?.locations,
	(newLocations) => {
		if (newLocations) {
			// locations.value = newLocations;
			suggestions.value = newLocations;
			isLoading.value = false;
		} else {
			isLoading.value = true;
		}
	},
	{ immediate: true },
);
</script>

<template>
	<div
		id="school-location"
		:class="{ loading: isLoadingArchive }"
	>
		<school-management
			v-if="schoolStore.schoolData && schoolStore.schoolData.locations"
			:action-btn-visible="true"
			:is-loading="isLoading"
			:page-icon="MaterialSymbolsPinDropOutlineRounded"
			:page-title="$t('app.school.SchoolLocations')"
			:create-btn-visible="true"
			:accordion-header="SchoolLocationHeader"
			:items="suggestions"
			:accordion-content="SchoolLocationContent"
			:colums="dataTableColumns"
			:speech-bubble-text="speechBubble"
			:guide-image="guideImage"
			@search-input="(e) => getSuggestions(e)"
			@edit-click="(e) => editSchoolLocation(e as TSchoolLocation)"
			@archive-click="(e) => deleteLocation(e as TSchoolLocation)"
			@create-new="openCreateSchoolLocationModal"
		/>
	</div>
</template>

<style lang="scss" scoped>
#school-location {
	max-height: calc(100vh - $nav-top-bar - 1px);
	overflow-y: auto;
	overflow-x: auto;
	box-sizing: border-box;

	&.loading {
		cursor: wait;
		pointer-events: none;
	}
}

@media screen and (width >= 768px) {
	#school-location {
		max-width: calc(100vw - $mini-sidebar-width);
	}
}

@media screen and (width >= 1157px) {
	#school-location {
		max-width: calc(100vw - $expanded-sidebar-width);
	}
}
</style>
